import React, { useState, useEffect } from "react";
import { API_URL } from "../../config";
import axios from "axios";
import { showToast } from "../../components/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faPencil,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { IoMdSearch } from "react-icons/io";
import { ImEye } from "react-icons/im";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const SetSalary = () => {
  const [allowances, setAllowances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const [isEditMode, setIsEditMode] = useState(false);
  const [currentAllowance, setCurrentAllowance] = useState({
    id: null,
    title: "",
    formula: "",
    is_prorated: false,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [allowanceList, setAllowanceList] = useState([]);
  const [salaryOptions, setSalaryOptions] = useState([]);
  const [variableMapping, setVariableMapping] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    id: null,
    title: "",
  });

  const allowanceCategories = [
    { id: 1, name: "Allowance", salary_type: "allowance" },
    { id: 2, name: "Statutory Deduction", salary_type: "statutory_deduction" },
    { id: 3, name: "Other Allowance", salary_type: "other_allowance" },
    { id: 4, name: "Other Deduction", salary_type: "other_deduction" },
    {
      id: 5,
      name: "Company Calculation",
      salary_type: "calculation",
      note: "(Not Part of Payslip)",
    },
  ];

  const fetchSalaryOptions = async (salary_id = 0) => {
    try {
      const response = await axios.get(
        `${API_URL}payroll/option/${salary_id || 0}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 1 && Array.isArray(response.data.data)) {
        const options = response.data.data.map((option) => ({
          id: option.id,
          title: option.title,
          value: option.id.toString(),
          label: option.title,
        }));

        setSalaryOptions(options);
        return options;
      }
      return [];
    } catch (error) {
      console.error("Error fetching salary options:", error);
      return [];
    }
  };

  const handleFormulaChange = (e) => {
    const formula = e.target.value;
    setCurrentAllowance((prev) => ({ ...prev, formula }));

    const variables = formula.match(/{{(\d+)}}/g) || [];
    const uniqueVars = [...new Set(variables)];

    setVariableMapping((prevMapping) =>
      uniqueVars.map((variable, index) => {
        const existingMapping = prevMapping.find(
          (m) => m.variable === variable
        );
        return {
          variable,
          value: existingMapping ? String(existingMapping.value) : "0",
          index,
        };
      })
    );
  };

  const handleVariableMappingChange = (selectedOption, index) => {
    const newMapping = [...variableMapping];
    newMapping[index].value = selectedOption ? String(selectedOption.value) : "0";
    setVariableMapping(newMapping);
  };

  const fetchAllowanceList = async (category) => {
    try {
      if (!category || !category.salary_type) {
        console.error("Invalid category:", category);
        return;
      }

      setLoading(true);

      const response = await axios.post(
        `${API_URL}payroll/list`,
        {
          salary_type: category.salary_type,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data.status) {
        showToast.error(
          response.data.message || "Failed to fetch allowance list"
        );
        setAllowanceList([]);
        return;
      }

      const allowances = response.data.data || [];
      const filteredAllowances = allowances
        .filter((allowance) => allowance.salary_type === category.salary_type)
        .map((allowance) => ({
          ...allowance,
          in_report: Boolean(allowance.in_report),
          is_prorated: Boolean(allowance.is_prorated),
        }));

      const transformedAllowances = filteredAllowances.map((allowance) => {
        const variables = allowance.formula.match(/{{(\d+)}}/g) || [];
        const uniqueVars = [...new Set(variables)];
        const variable_mapping =
          allowance.variable_mapping ||
          uniqueVars.map((variable, index) => ({
            variable,
            value: "0",
            index,
          }));

        return {
          ...allowance,
          variable_mapping,
        };
      });

      setAllowanceList(transformedAllowances);
    } catch (error) {
      console.error("Error fetching allowance list:", error);
      setError(error.message);
      showToast.error("Failed to fetch allowance list");
      setAllowanceList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowanceCategories.length > 0) {
      setSelectedCategory(allowanceCategories[0]);
      fetchAllowanceList(allowanceCategories[0]);
      fetchSalaryOptions();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!currentAllowance.title || !currentAllowance.formula) {
      showToast.error("Title and formula are required");
      setIsLoading(false);
      return;
    }

    try {
      const url = `${API_URL}payroll/save`;

      // Transform variable mapping into separate arrays
      const variables = variableMapping.map((m) =>
        String(m.variable.replace(/[{}]/g, ""))
      );
      const values = variableMapping.map((m) => String(m.value));

      const payload = {
        salary_id: isEditMode ? currentAllowance.id : undefined, // Changed to salary_id for update
        title: currentAllowance.title,
        formula: currentAllowance.formula,
        salary_type: selectedCategory.salary_type,
        variable: variables,
        value: values,
        is_prorated: !!currentAllowance.is_prorated,
      };

      const response = await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.status === 1) {
        showToast.success(response.data.message || "Saved successfully");
        closeModal();
        setCurrentAllowance({ id: null, title: "", formula: "", is_prorated: false });
        setVariableMapping([]);
        setIsEditMode(false);
        fetchAllowanceList(selectedCategory);
      } else {
        showToast.error(response.data.message || "Failed to save");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showToast.error(
        error.response?.data?.message ||
        "An error occurred. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (id, title) => {
    setDeleteModal({
      show: true,
      id,
      title,
    });
  };

  const handleDelete = async () => {
    if (!deleteModal.id) return;

    try {
      const response = await axios.delete(
        `${API_URL}payroll/delete/${deleteModal.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 1 || response.data.success) {
        showToast.success(response.data.message || "Deleted successfully");
        fetchAllowanceList(selectedCategory);
      } else {
        // Custom error for dependency
        if (
          response.data.message &&
          response.data.message.includes(
            "being used in other salary calculations"
          )
        ) {
          showToast.error(
            "Cannot delete: This salary is used in other salary calculations. Remove its usage from all formulas before deleting."
          );
        } else {
          showToast.error(
            response.data.message || "Failed to delete allowance"
          );
        }
      }
    } catch (error) {
      showToast.error(
        error.response?.data?.message || "Failed to delete allowance"
      );
    } finally {
      setDeleteModal({ show: false, id: null, title: "" });
    }
  };

  // Toggle a boolean flag (in_report / is_prorated) with optimistic UI update
  const toggleAllowanceFlag = async (allowance, field, endpoint, errorMessage) => {
    const newValue = !allowance[field];
    const setFlag = (value) =>
      setAllowanceList((currentList) =>
        currentList.map((item) =>
          item.id === allowance.id ? { ...item, [field]: value } : item
        )
      );

    setFlag(newValue);
    try {
      const response = await axios.post(
        `${API_URL}${endpoint}`,
        {
          id: allowance.id,
          [field]: newValue,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.status === 1) {
        showToast.success(response.data.message || "Updated successfully");
      } else {
        setFlag(!newValue);
        showToast.error(response.data.message || "Failed to update");
      }
    } catch (error) {
      setFlag(!newValue);
      showToast.error(error.response?.data?.message || errorMessage);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentAllowance({ id: null, title: "", formula: "", is_prorated: false });
    setVariableMapping([]);
    setIsEditMode(false);
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, id: null, title: "" });
  };


  return (
    <div className="page-header-fixed page-sidebar-fixed mt-5">
      <div id="content">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">
              {selectedCategory
                ? "Manage " + selectedCategory.name
                : "Manage Allowances"}
            </h4>
          </div>
          <div className="col-md-6">
            <PermissionWrapper module="payroll_set_salary" action="add">
              <button
                className="btn btn-default btn-sm pull-right addModel ms-2"
                onClick={async () => {
                  setIsEditMode(false);
                  setCurrentAllowance({ id: null, title: "", formula: "", is_prorated: false });
                  setVariableMapping([]);
                  await fetchSalaryOptions(0);
                  setIsModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} />{" "}
                <span id="add-title">
                  {selectedCategory
                    ? "Add " + selectedCategory.name
                    : "Add Allowance"}
                </span>
              </button>
            </PermissionWrapper>
            <button
              className="btn btn-default btn-sm pull-right addModel ms-2"
              onClick={() => navigate("/Payroll/SetSalary/viewReportOrder")}
            >
              <IoMdSearch size={18} />
              Sorting Report
            </button>
            <button
              className="btn btn-default btn-sm pull-right addModel ms-2"
              onClick={() => navigate("/Payroll/SetSalary/viewSalaryOrder")}
            >
              <IoMdSearch size={18} />
              Sorting Formula
            </button>
            <button
              className="btn btn-default btn-sm pull-right addModel"
              onClick={() => navigate("/Payroll/SetSalary/demo")}
            >
              <ImEye size={16} />
              Demo Salary
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-md-3">
            <div className="card">
              <div className="list-group border">
                {allowanceCategories.map((category) => (
                  <button
                    key={category.id}
                    className={`list-group-item list-group-flush list-group-item-action border-0 ${selectedCategory?.id === category.id ? "active" : ""
                      }`}
                    onClick={() => {
                      setSelectedCategory(category);
                      setAllowanceList([]);
                      fetchAllowanceList(category);
                    }}
                    style={{ padding: "12px 20px" }}
                  >
                    {category.name}
                    {category.note && (
                      <small className="text-danger">{category.note}</small>
                    )}
                    <div className="pull-right">
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="col-md-9">
            <div className="card">
              <div className="card-body border">
                <div className="table-responsive">
                  <style>
                    {`
                      .skeleton-box {
                        height: 15px;
                        background: #f2f2f2;
                        background: linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%);
                        background-size: 200% 100%;
                        animation: skeleton-loading 1.5s infinite;
                        border-radius: 4px;
                        width: 100%;
                      }

                      @keyframes skeleton-loading {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                      }
                    `}
                  </style>
                  <table className="table" id="simple-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Formula</th>
                        <th>In Report</th>
                        <th>Prorate by days</th>
                        <th width="200px">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading || isFiltering ? (
                        Array.from({ length: 8 }).map((_, index) => (
                          <tr key={index}>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" style={{ width: "40px" }} /></td>
                            <td><div className="skeleton-box" style={{ width: "40px" }} /></td>
                            <td><div className="skeleton-box" style={{ width: "100px" }} /></td>
                          </tr>
                        ))
                      ) : allowanceList.length > 0 ? (
                        allowanceList.map((allowance) => (
                          <tr key={allowance.id}>
                            <td>{allowance.title}</td>
                            <td>{allowance.parse_formula || ""}</td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={!!allowance.in_report}
                                  onChange={() =>
                                    toggleAllowanceFlag(
                                      allowance,
                                      "in_report",
                                      "payroll/in-report",
                                      "Failed to update in-report status"
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={!!allowance.is_prorated}
                                  onChange={() =>
                                    toggleAllowanceFlag(
                                      allowance,
                                      "is_prorated",
                                      "payroll/is-prorated",
                                      "Failed to update prorate status"
                                    )
                                  }
                                />
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <PermissionWrapper
                                  module="payroll_set_salary"
                                  action="edit"
                                >
                                  <button
                                    className="btn btn-warning btn-xs"
                                    onClick={async () => {
                                      try {
                                        setIsEditMode(true);
                                        setCurrentAllowance({
                                          id: allowance.id,
                                          title: allowance.title,
                                          formula: allowance.formula,
                                          is_prorated: !!allowance.is_prorated,
                                        });

                                        // 1. Fetch available options for this allowance
                                        await fetchSalaryOptions(allowance.id);

                                        // 2. Fetch single salary details including single_salary_metas from backend
                                        const res = await axios.get(
                                          `${API_URL}payroll/single/${allowance.id}`,
                                          {
                                            headers: {
                                              Authorization: `Bearer ${token}`,
                                              "Content-Type": "application/json",
                                            },
                                          }
                                        );

                                        const singleSalary =
                                          res.data?.data?.single_salary;
                                        const singleMetas =
                                          res.data?.data?.single_salary_metas ||
                                          [];

                                        if (singleSalary) {
                                          setCurrentAllowance({
                                            id: singleSalary.id,
                                            title: singleSalary.title,
                                            formula: singleSalary.formula,
                                            is_prorated: Boolean(
                                              singleSalary.is_prorated
                                            ),
                                          });
                                        }

                                        const formula =
                                          (singleSalary
                                            ? singleSalary.formula
                                            : allowance.formula) || "";
                                        const variables =
                                          formula.match(/{{(\d+)}}/g) || [];
                                        const uniqueVars = [
                                          ...new Set(variables),
                                        ];

                                        const newMapping = uniqueVars.map(
                                          (variable, index) => {
                                            const varNumber = variable.replace(
                                              /[{}]/g,
                                              ""
                                            );
                                            const matchedMeta =
                                              singleMetas.find(
                                                (m) =>
                                                  String(m.variable) ===
                                                  varNumber
                                              ) || singleMetas[index];

                                            return {
                                              variable,
                                              value: matchedMeta
                                                ? String(matchedMeta.value)
                                                : "0",
                                              index,
                                            };
                                          }
                                        );

                                        setVariableMapping(newMapping);
                                        setIsModalOpen(true);
                                      } catch (err) {
                                        console.error(
                                          "Error loading single salary:",
                                          err
                                        );
                                        showToast.error(
                                          "Failed to load allowance details"
                                        );
                                      }
                                    }}
                                    title="Edit"
                                  >
                                    <FontAwesomeIcon icon={faPencil} />
                                  </button>
                                </PermissionWrapper>
                                <PermissionWrapper
                                  module="payroll_set_salary"
                                  action="delete"
                                >
                                  <button
                                    className="btn btn-danger btn-xs"
                                    onClick={() =>
                                      confirmDelete(
                                        allowance.id,
                                        allowance.title
                                      )
                                    }
                                    title="Delete"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </PermissionWrapper>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" style={{ textAlign: "center" }}>
                            No allowances available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1040,
            }}
          ></div>

          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="allowanceModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-md">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header justify-content-between py-2">
                    <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                      {isEditMode ? "Edit" : "Add"} {selectedCategory?.name}
                    </h5>
                    <button
                      type="button"
                      className="btn"
                      style={{ fontSize: "25px", border: "none" }}
                      onClick={closeModal}
                    >
                      &times;
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="row">
                      <div className="form-group col-12 mb-3">
                        <label className="col-form-label">Title</label>
                        <input
                          className="form-control"
                          placeholder="Enter Allowance Title"
                          type="text"
                          value={currentAllowance.title}
                          onChange={(e) =>
                            setCurrentAllowance({
                              ...currentAllowance,
                              title: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="form-group col-12 mb-3">
                        <label className="col-form-label">Formula</label>
                        <input
                          className="form-control"
                          placeholder="Enter Formula (use {{1}}, {{2}} etc. for variables)"
                          type="text"
                          value={currentAllowance.formula}
                          onChange={handleFormulaChange}
                        />
                      </div>
                      <div className="form-group col-12 mb-3">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="is_prorated"
                            checked={!!currentAllowance.is_prorated}
                            onChange={(e) =>
                              setCurrentAllowance({
                                ...currentAllowance,
                                is_prorated: e.target.checked,
                              })
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="is_prorated"
                          >
                            Prorate by days
                          </label>
                        </div>
                        <small className="text-muted">
                          Earned value = fixed value × (present days / total
                          days in month)
                        </small>
                      </div>
                      {variableMapping.length > 0 && (
                        <div className="col-12">
                          <div className="mt-2">
                            <p>
                              <b>Variable Mapping</b>
                            </p>
                            <table className="table table-borderless table-sm">
                              <thead>
                                <tr>
                                  <th>Variable</th>
                                  <th>Field</th>
                                </tr>
                              </thead>
                              <tbody>
                                {variableMapping.map((mapping, index) => (
                                  <tr key={index}>
                                    <td>{mapping.variable}</td>
                                    <td>
                                      <Select
                                        value={
                                          [
                                            { value: "0", label: "Salary" },
                                            ...salaryOptions,
                                          ].find(
                                            (opt) =>
                                              String(opt.value) ===
                                              String(mapping.value)
                                          ) || { value: "0", label: "Salary" }
                                        }
                                        onChange={(opt) =>
                                          handleVariableMappingChange(
                                            opt,
                                            index
                                          )
                                        }
                                        options={[
                                          { value: "0", label: "Salary" },
                                          ...salaryOptions,
                                        ]}
                                        className="basic-single"
                                        classNamePrefix="select"
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading && <LoadingSpinner size="sm" />}
                      {isLoading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
            }}
          ></div>

          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="deleteModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeDeleteModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete{" "}
                    <strong>{deleteModal.title}</strong>?
                  </p>
                  <p>This action cannot be undone.</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SetSalary;
